import { DESIGN_OPTIONS } from 'constants/energyAdvisor';
import { edelap } from 'constants/mapCoordinates';
import { UTILITIES } from 'constants/utilitiesMappers';
import { PAYMENT_GATEWAYS } from 'constants/payments';

import Colors from './colors/edelap';
import SassColors from './edelap/scss/_colors.scss';

const DOCUMENT = 'documentType';

const utilitySettings = {
  i18n: 'Edelap',
  colors: Colors,
  sassColors: SassColors,
  assets: 'edelap',
  appTitle: 'EDELAP',
  utilityId: 9,
  canChangePrimaryEmail: false,
  mappers: UTILITIES.edelap,
  includesHolderChangeProcedure: true,
  includesTerminationService: true,
  includesRegisterAccount: true,
  usePayments: true,
  usePaymentDocuments: true,
  usePoints: false,
  deleteAccount: false,
  shouldUseAccOptimization: false,
  ACCOUNT_LENGTH: 9,
  clientNumberRegex: /^[1-9]{1}[0-9]{8}$/,
  lineSections: false,
  showUserAccountRelationship: false,
  useClientSummary: true,
  useChatbot: false,
  canUseDashboardWithoutAccounts: true,
  fontFamily: 'Swiss',
  mobileSidebarLogo: false,
  mobileTopbarLogo: false,
  disabledMobileAvatarEnergyAdvisor: false,
  turns: {
    active: true
  },
  // Service Adherence
  automaticDebitShortcuts: true,
  digitalBillShortcuts: true,
  userDigitalBillAdherence: false,
  digitalBillWithForm: true,
  // Sign in configuration
  signInMethod: DOCUMENT,
  passwordRequireNumberAndLetter: true,
  passwordMinLength: 8,
  passwordMaxLength: 255,
  PASSWORD_CHANGE_EXTERNAL_TOKEN: 'edelap_token', // TODO: Try to change this 'edelap_token' with something more generic ant put in signin constants
  hideAppTitle: true,
  authAvatarSquared: false,
  authAvatarWithShadow: true,
  authAvatarWithBackground: true,
  authBackgroundColorHorizontal: false,
  authImageBackground: false,
  authLogoInForm: false,
  authFormCentered: true,
  buttonRadius: 4,
  squaredToggle: false,
  payExternalAcc: true,
  errorScreenWithImage: true,
  analytics: {
    trackDigitalBillOnRegister: true
  },
  withTooltip: false,
  withAppDownloader: false,
  accountSelectKeys: [
    { prefix: '', key: 'client_number' },
    { prefix: '-', key: 'address' }
  ],
  appleStoreURL: 'https://itunes.apple.com/ar/app/edelap/id1376930003?mt=8',
  googlePlayStoreURL: 'https://play.google.com/store/apps/details?id=com.utility.edelap&hl=es',
  googleMap: {
    defaultCoordinates: {
      latitude: -34.911934,
      longitude: -57.948831
    },
    colors: {
      polygon: Colors.primary,
      borderPolygon: Colors.primary
    },
    zoom: {
      initial: 10,
      addressValid: 15,
      addressInvalid: 10
    },
    withPolygon: true,
    withGeolocation: true,
    path: edelap
  },
  signIn: {
    conditionalRequestGender: true,
    minDocNumber: 10000,
    maxDocNumber: 10000000,
    iconField: false
  },
  UTModal: {
    modalBorderRadius: 7
  },
  // Sign up flow
  signUpFlow: {
    PersonalInformation: true,
    IdentityValidation: true,
    UserInformation: true,
    TermsAndConditions: true,
    EmailValidation: true,
    AddAccount: false,
    AccountRelationshipValidation: false,
    ServiceAdherence: false,
    RelatedAccounts: false
  },
  signUp: {
    retrieveUserIdentification: true,
    personalInformationFields: {
      document_type: { show: true, required: true },
      platform_discovery: { show: false, required: false },
      document_number: { show: true, required: true },
      gender: { show: true, required: true },
      name: { show: true, required: true },
      last_name: { show: true, required: true },
      bornDate: { show: true, required: true },
      carrier: { show: true, requiredOn: 'cellNumber' },
      cellNumber: { show: true, required: false, dependsOf: 'phone' },
      phone: { show: true, required: false, dependsOf: 'cellNumber' },
      contact_first_name: { show: true, required: true },
      contact_last_name: { show: true, required: true },
      captcha: { show: false, required: true }
    },
    personalInformationFullWidthFields: false,
    nameMaxLength: 999,
    lastNameMaxLength: 999
  },

  // Dashboard configuration
  showAmountChart: false,
  showAmountComparison: false,
  showAnnualProgress: false,
  topbarUtilityLogo: true,
  topbarCustomIcons: false,
  topbar: {},
  utilityLinks: [{ text: 'Información útil', url: 'http://www.edelap.com.ar/informacion-util' }],
  addSupplies: false,
  simulatorLink: 'https://simulador.edelap.com.ar/',
  simulatorButton: true,
  accountStatus: {
    consumptionHistory: true,
    consumptionHistoryPeriods: 13,
    digitalBillButtonWithTitle: false
  },

  // Bills configuration
  billsPageSize: 12,
  disableBillsPagination: true,
  PaymentDocumentSelection: true,
  encodedDocumentDownload: false,
  useBillsBalance: true,
  useBillsSurchargeAmount: false,
  multipleBillsDownload: true,
  claims: {
    useClaims: true,
    useClaimList: true,
    openableRow: false,
    openClaimsFromSidebar: false,
    showAccountSelector: true,
    cancelClaimComment: true,
    reiterationClaimComment: true
  },
  procedures: {
    useProcedures: true,
    useProcedureList: false,
    showAccountSelector: true
  },
  bills: {
    billDownloadOnPDF: true,
    lastBill: false,
    downloadIcon: false,
    downloadIconBillsScreen: false,
    downloadIconMobile: false,
    showAccountSelector: true
  },
  billsColumns: {
    period: { width: 0.07, order: 1 },
    issued_on: { width: 0.09, order: 2 },
    document_type: { width: 0.17, order: 3 },
    document_number: { width: 0.11, order: 4 },
    first_expiration_on: { width: 0.1, order: 5 },
    status: { width: 0.13, order: 6 },
    amount: { width: 0.09, order: 7 },
    balance: { width: 0.09, order: 8 },
    download: { width: 0.09, order: 9 }
  },

  survey: {
    payment: true,
    procedure: true,
    claim: false
  },

  payments: {
    paymentForNis: false,
    paymentsWithEmail: false,
    paymentDocumentDownloadOnPDF: true,
    active: true,
    publicPayment: true,
    publicPaymentByNis: false,
    multipleDownloadRow: true,
    paymentDocumentModal: false,
    columns: {
      checkbox: { show: true, width: 0.04, order: 1, showOnMobile: true },
      period: { show: true, width: 0.06, order: 2, showOnMobile: true },
      issued_on: { show: false, width: 0.08, order: 2, showOnMobile: false },
      document_type: { show: true, width: 0.15, order: 3, showOnMobile: true },
      document_number: { show: true, width: 0.13, order: 4, showOnMobile: true },
      first_expiration_on: { show: true, width: 0.11, order: 5, showOnMobile: true },
      status: { show: true, width: 0.14, order: 6, showOnMobile: true },
      amount: { show: true, width: 0.11, order: 7, showOnMobile: true },
      balance: { show: true, width: 0.12, order: 8, showOnMobile: true },
      download: { show: true, width: 0.12, order: 9, showOnMobile: true },
      barcode: { show: false, width: 0.2, order: 4, showOnMobile: false },
      external_id: { show: false, width: 0.13, order: 5, showOnMobile: false }
    }
  },

  maxAmountThreshold: 100000,
  maxDocsThreshold: 10000000000,

  // Public payment configuration
  userDataRequired: false, // email or doc not required for coupon
  allowPaymentDocumentSelection: true, // if true, it goes to payment screen after pressing coupon button
  allowMultipleCoupons: true,
  showPaymentPlaces: true,

  // Settings view configuration
  userDataManagement: {
    landlineRequired: true,
    cellPhoneRequired: true,
    phoneRequired: true,
    defaultPhoneRequired: true,
    requirePasswordForUserDataManagement: true,
    useCarrier: true,
    useDefaultPhone: true,
    shouldUpdateEmail: true,
    mayDeletePhones: true,
    MAX_PHONES: 999,
    usePhoneFields: false,
    emailMaxLength: 50,
    rowDescIcons: false,
    addPhoneButton: true,
    userConfigBlueMargin: true
  },

  dashboard: {
    pendingFineAmountQuery: true,
    pendingWarrantyDepositQuery: true
  },

  publicDashboard: {
    publicPayment: true,
    lastBillQuery: false,
    balanceQuery: false,
    procedures: false,
    claims: false,
    complaints: false,
    pendingFineAmountQuery: true,
    pendingWarrantyDepositQuery: true,
    socialRate: true,
    home: false,
    changeDueDate: false,
    usePublicBanner: false,
    simulator: true
  },

  homeButtonWithClientNumber: false,

  clientSummary: {
    selectAllAccounts: false,
    multipleAccountsPayment: false,
    useStatusColumn: true
  },

  forms: {
    showFormCategoriesListTitle: false
  },
  socialRate: {
    externalSearchLink: 'https://oceba.gba.gov.ar/tarifa_social/pordni',
    useSocialRate: true,
    content: [
      {
        subtitle:
          'La Tarifa Social fue concebida para que los ciudadanos en situación de mayor vulnerabilidad paguen un precio más bajo por los servicios públicos, entre ellos el de electricidad.'
      },
      {
        title: 'Esquema de bonificación',
        subtitle:
          'La bonificación en el precio mayorista de energía, aplicable a los usuarios encuadrados en la TARIFA SOCIAL es de un 100 % para los primeros 150 KWh de consumo/mes y un 50% para los segundos 150KWh/mes, de acuerdo a lo establecido por las Autoridades Nacionales. No obstante, en la provincia de Buenos Aires, la tarifa del distribuidor además del precio mayorista de energía se integra con otros parámetros distintos de cero (0). En razón de ello, si bien la facturación de un usuario en TS no es igual a 0, toma un valor inferior al de una categoría residencial para ese rango de consumo. Por ejemplo, un usuario encuadrado en Tarifa Social que consume 150 KWh/mes abonará un 70% menos que una Tarifa Residencial de igual consumo; hasta 300KWh-mes, abonará un 40 % menos que igual consumo de una Tarifa Residencial. Después de los 300KWh el descuento sigue disminuyendo a razón de 6% cada 100KWh-mes. La facturación del distribuidor debe ser escalonada, discriminando el valor de los primeros 150KWh-mes, segundos 150KWh-mes y luego el resto.'
      },
      {
        title: 'Acceden a este beneficio los usuarios que sean:',
        items: [
          {
            content:
              'Jubilados, pensionados o trabajadores en relación de dependencia que perciban una remuneración bruta menor o igual a DOS (2) Salarios Mínimos, Vitales y Móviles.'
          },
          {
            content:
              'Trabajadores “monotributistas” inscriptos en una categoría cuyo ingreso anual mensualizado no supere en dos (2) veces el Salario Mínimo, Vital y Móvil.'
          },
          {
            content: 'Beneficiarios de una Pensión no Contributiva que perciban ingresos mensuales'
          },
          {
            content:
              'Titulares de programas sociales (siempre que su ingreso no supere dos (2) veces el Salario Mínimo, Vital y Móvil).'
          },
          {
            content:
              'Trabajadores inscriptos en el Régimen de Monotributo Social (siempre que su ingreso no supere dos (2) veces el Salario Mínimo, Vital y Móvil).'
          },
          {
            content:
              'Trabajadores incorporados en el Régimen Especial de Seguridad Social para empleados del Servicio Doméstico (26.844) (siempre que su ingreso no supere dos (2) veces el Salario Mínimo, Vital y Móvil).'
          },
          {
            content:
              'Titulares de un seguro de desempleo (siempre que su ingreso no supere dos (2) veces el Salario Mínimo, Vital y Móvil).'
          },
          {
            content: 'Titulares de una Pensión Vitalicia a Veteranos de Guerra del Atlántico Sur.'
          },
          {
            content:
              'Usuarios (o un familiar a cargo) que cuenten con certificado de discapacidad expedido por autoridad competente.'
          }
        ],
        withBullet: true
      },
      {
        title: 'Atención',
        subtitle: `No acceden a este beneficio los propietarios de:

1. Más de un inmueble
2. Aeronaves o embarcaciones de lujo
3. Un vehículo de hasta diez años de antigüedad (excepto los titulares de algún certificado de discapacidad).`
      }
    ],
    showClientNumber: true,
    showAddress: true,
    manualInput: true,
    showCancelButton: false
  },

  pendingWarrantyDepositQuery: {
    content: `Aquí usted podrá visualizar si registra un Depósito en garantía a disposición para reintegro. (Esta consulta solo es posible para los suministros dados de baja)\n <br> IMPORTANTE: En caso de registrarse deudas vencidas con las Distribuidora, se procederá previamente a su cancelación, procediéndose al reintegro en la parte del mismo que no hubiera sido imputado a su saldo de deuda.\n <br> **NOTA LEGAL: Si transcurrido un (1) año desde que se encuentra a su disposición EL USUARIO no hubiera retirado el depósito de garantía, EL DISTRIBUIDOR deberá afectar dicho importe a proyectos sociales vinculados al servicio público de distribución de energía eléctrica, realizando la correspondiente presentación al ORGANISMO DE CONTROL para su toma de conocimiento y debida autorización.*
    `
  },

  pendingFineAmountQuery: {
    content: `Aquí usted podrá visualizar si registra una Bonificación por calidad de servicio a disposición para reintegro. (Esta consulta solo es posible para los suministros dados de baja)`
  },

  complaints: {
    includeInTechnicalService: false,
    useComplaints: true,
    withSubtitles: true,
    complaintsForm: false,
    withCrimeAlert: false,
    withBullets: true,
    withLine: true,
    squaredContactItems: false,
    bullets: [
      { content: 'Crea peligros mortales de incendio y seguridad.', id: 1 },
      {
        content:
          'El robo puede resultar en lesiones o muerte para aquellos que roban la energía, sus familias, vecinos y empleados de servicios públicos.',
        id: 2
      },
      { content: 'El robo de energía también puede causar daños materiales costosos.', id: 3 },
      { content: 'El robo de energía causa problemas en la calidad del servicio.', id: 4 }
    ],
    contactPhone: '0800-222-3335',
    contactMail: 'infoedelap@edelap.com.ar'
  },
  energyAdvisor: {
    design: DESIGN_OPTIONS[2],
    tipsWithTitle: false
  },

  changeDueDate: {
    active: true
  },

  paymentFlow: {
    useCardSelectionStep: true,
    provider: PAYMENT_GATEWAYS.DECIDIR,
    paymentMethods: [
      { id: 10, name: 'Visa crédito', value: 'Visa', order: 5, key: 1 },
      { id: 11, name: 'MasterCard', value: 'Mastercard', order: 2, key: 2 },
      { id: 12, name: 'Nativa', value: 'TarjetaNativa', order: 4, key: 3 },
      { id: 13, name: 'Visa débito', value: 'VisaDebito', order: 6, key: 4 },
      { id: 14, name: 'MasterCard Débito', value: 'Masterdebit', order: 3 },
      { id: 15, name: 'Maestro', value: 'Maestro', order: 1 }
    ]
  },

  balanceAndLastBillQuery: {
    showCancelButton: false
  },

  accountAlerts: {
    active: false,
    claimFormId: 72
  },

  alerts: {
    withoutIcon: false
  },

  accountPaymentDocsForm: {
    showCancelButton: false
  },

  imageResizing: {
    maxWidth: 1000,
    maxHeight: 1000,
    imageType: 'image/png'
  }
};

export default utilitySettings;
